const SCHEMA_CONTEXT = 'https://schema.org';

type Schema = Record<string, unknown>;

export interface OrganizationInput {
  name: string;
  legalName?: string;
  telephone?: string;
  email?: string;
}

export const absoluteUrl = (path: string): string => {
  const base = (process.env.IPOS_ROOT_URL || 'https://ipos.pl/').replace(/\/+$/, '');

  if (!path) {
    return base;
  }

  if (/^(https?:)?\/\//.test(path) || /^data:/.test(path)) {
    return path;
  }

  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
};

export const absoluteImageUrl = (path: string): string => {
  if (!path || /^(https?:)?\/\//.test(path) || /^data:/.test(path)) {
    return path;
  }

  const encoded = path
    .split('/')
    .map(segment => encodeURIComponent(segment))
    .join('/');

  return absoluteUrl(encoded);
};

export const getLogoUrl = (): string => absoluteUrl('/ipos-logo.svg');

const stripHtml = (html: string): string =>
  (html || '')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const getOrganizationRef = (name: string, logoUrl: string): Schema => ({
  '@type': 'Organization',
  name,
  url: absoluteUrl('/'),
  logo: {
    '@type': 'ImageObject',
    url: logoUrl,
  },
});

export const buildOrganizationSchema = (
  org: OrganizationInput,
  socialUrls: string[],
  logoUrl: string
): Schema => {
  const contactPoint: Schema = {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    areaServed: 'PL',
    availableLanguage: ['Polish'],
  };
  if (org.telephone) {
    contactPoint.telephone = org.telephone;
  }
  if (org.email) {
    contactPoint.email = org.email;
  }

  const schema: Schema = {
    '@context': SCHEMA_CONTEXT,
    '@type': 'Organization',
    name: org.name,
    url: absoluteUrl('/'),
    logo: {
      '@type': 'ImageObject',
      url: logoUrl,
    },
    contactPoint,
    sameAs: socialUrls.filter(Boolean),
  };
  if (org.legalName) {
    schema.legalName = org.legalName;
  }

  return schema;
};

interface ItemListEntry {
  name: string;
  description?: string;
}

export const buildItemListSchema = (items: ItemListEntry[], url: string): Schema => ({
  '@context': SCHEMA_CONTEXT,
  '@type': 'ItemList',
  itemListElement: (items || [])
    .filter(item => item && item.name)
    .map((item, index) => {
      const element: Schema = {
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        url,
      };

      const description = stripHtml(item.description || '');
      if (description) {
        element.description = description;
      }

      return element;
    }),
});

interface ArticleSchemaInput {
  headline: string;
  url: string;
  orgName: string;
  logoUrl: string;
  datePublished?: string;
  dateModified?: string;
  articleSection?: string;
  imageUrl?: string;
  description?: string;
}

export const buildArticleSchema = ({
  headline,
  url,
  orgName,
  logoUrl,
  datePublished,
  dateModified,
  articleSection,
  imageUrl,
  description,
}: ArticleSchemaInput): Schema => {
  const organization = getOrganizationRef(orgName, logoUrl);

  const schema: Schema = {
    '@context': SCHEMA_CONTEXT,
    '@type': 'Article',
    headline,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    author: organization,
    publisher: organization,
  };

  if (imageUrl) {
    schema.image = imageUrl;
  }
  if (datePublished) {
    schema.datePublished = datePublished;
  }
  if (dateModified) {
    schema.dateModified = dateModified;
  }
  if (articleSection) {
    schema.articleSection = articleSection;
  }
  if (description) {
    schema.description = stripHtml(description);
  }

  return schema;
};
